"use client";

import { FormEvent, useState } from "react";
import { ImageUp, Save } from "lucide-react";
import type { CompanyProfile } from "@/lib/types";

const EMPTY: CompanyProfile = {
  id: "primary",
  legalName: "Aqua Software Inc.",
  displayName: "Aqua Homes",
  address: "",
  phone: "",
  email: "",
  website: "https://draw.aquahomesdesigngroup.com",
  logoUrl: "/aqua-homes-watermark.png",
};

export function CompanyProfileSettings({
  profile,
  updateCompanyProfile,
  setMessage,
}: {
  profile: CompanyProfile | null;
  updateCompanyProfile: (input: Omit<CompanyProfile, "id">, logoFile?: File) => Promise<CompanyProfile>;
  setMessage: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | undefined>();
  const [logoPreview, setLogoPreview] = useState("");
  const current = profile || EMPTY;
  const toggleEditing = () => {
    setEditing(!editing);
    setLogoFile(undefined);
    setLogoPreview("");
  };
  const chooseLogo = (file?: File) => {
    setLogoFile(file);
    setLogoPreview("");
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(String(reader.result));
    reader.readAsDataURL(file);
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await updateCompanyProfile({
        legalName: String(form.get("legalName")),
        displayName: String(form.get("displayName")),
        address: String(form.get("address")),
        phone: String(form.get("phone")),
        email: String(form.get("email")),
        website: String(form.get("website")),
        logoUrl: current.logoUrl || "/aqua-homes-watermark.png",
      }, logoFile);
      setEditing(false);
      setLogoFile(undefined);
      setLogoPreview("");
      setMessage("Company invoice information saved and verified.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save company invoice information.");
    } finally {
      setSaving(false);
      window.setTimeout(() => setMessage(""), 3200);
    }
  };

  return <div className="panel">
    <div className="section-head"><div><span className="eyebrow">Client Invoice Identity</span><h2>Invoice Information</h2></div><button data-aqua-id="edit-company-invoice-identity" className="secondary-button" type="button" onClick={toggleEditing}>{editing ? "Cancel" : "Edit"}</button></div>
    <p>{profile ? "This information appears on each client invoice." : "Complete this once before a client can export an invoice."}</p>
    {editing ? <form className="manager-form" onSubmit={submit}>
      <label>Legal company name<input data-aqua-field="legal-name" data-aqua-label="Legal company name" name="legalName" defaultValue={current.legalName} required /></label>
      <label>Display name<input data-aqua-field="display-name" data-aqua-label="Company display name" name="displayName" defaultValue={current.displayName} required /></label>
      <label>Company address<textarea data-aqua-field="company-address" data-aqua-label="Company address" name="address" rows={2} defaultValue={current.address} required /></label>
      <label>Phone<input data-aqua-field="company-phone" data-aqua-label="Company phone" name="phone" defaultValue={current.phone} required /></label>
      <label>Email<input data-aqua-field="company-email" data-aqua-label="Company email" name="email" type="email" defaultValue={current.email} required /></label>
      <label>Website<input data-aqua-field="company-website" data-aqua-label="Company website" name="website" type="url" defaultValue={current.website} required /></label>
      <label>Company logo<img src={logoPreview || current.logoUrl} alt={`${current.displayName} invoice logo`} style={{ width: 96, height: 96, objectFit: "contain", display: "block", margin: "10px 0" }} /><span><ImageUp size={16} /> Upload PNG, JPEG, or WebP</span><input data-aqua-manual-only="file-picker" name="logo" type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => chooseLogo(event.target.files?.[0])} /></label>
      <button data-aqua-id="save-company-invoice-identity" data-aqua-consequence="state_change" className="primary" type="submit" disabled={saving}><Save size={17} />{saving ? "Saving Company Information…" : "Save Company Information"}</button>
    </form> : <div className="job-details-grid"><div><span>Logo</span><img src={current.logoUrl} alt={`${current.displayName} invoice logo`} style={{ width: 72, height: 72, objectFit: "contain" }} /></div><div><span>Company</span><strong>{current.legalName}</strong></div><div><span>Display name</span><strong>{current.displayName}</strong></div><div><span>Address</span><strong>{current.address || "Not set"}</strong></div><div><span>Phone</span><strong>{current.phone || "Not set"}</strong></div><div><span>Email</span><strong>{current.email || "Not set"}</strong></div><div><span>Website</span><strong>{current.website || "Not set"}</strong></div></div>}
  </div>;
}
